import { useQuery } from '@tanstack/react-query';
import { SearchX, Users } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { groupsOf, type AccessGroup } from '@/api/auth';
import { considerationsQuery, type ConsiderationRow } from '@/api/considerations';
import { useSession } from '@/app/session';
import { ApiErrorState } from '@/components/states/ApiErrorState';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Score } from '@/components/ui/Score';
import { StatusDot } from '@/components/ui/StatusDot';
import { Table, type Column } from '@/components/ui/Table';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/cn';
import { type } from '@/theme/typography';

import { AddCandidateButton } from './AddCandidateButton';
import { AddCandidateDialog } from './AddCandidateDialog';
import { mayAddCandidate } from './candidateAccess';
import {
  CANDIDATE_PAGE_SIZE,
  candidateSearch,
  couldBeHidingRows,
  readCandidateFilters,
  toConsiderationQuery,
  type CandidateFilterInput,
  type CandidateFilters,
} from './candidateFilters';
import { ClearFilters, FilterBar } from './FilterBar';
import { absoluteUpdatedAt, relativeUpdatedAt } from './updatedAt';

/**
 * `DESIGN.md` §6.3 — the list the whole product navigates from.
 *
 * **A table of considerations, not of people.** One person considered for two vacancies is two rows
 * carrying the same `person_id`; that is ADR 0002's split made visible, not a duplicate to collapse,
 * and both rows link to the one card.
 *
 * ⚠️ **Feedback protection #3 (ADR 0006) is what the Average and Latest columns are.**
 * `average_score` and `latest_recommendation` are computed by the API over the **caller's own**
 * blind-filtered set (backend TASK-033), so a viewer holding any unsubmitted interview for a
 * candidate is sent `null` where a colleague who owes nothing is sent the figure — for an Admin and a
 * Recruiter too, because the gate keys on owing feedback and not on group. This screen therefore
 * renders both **exactly as sent**: it computes no aggregate, it issues no second request to fill one
 * in, and its cache key carries the viewer (`considerationsQuery`, whose docblock records the
 * `placeholderData` that walked around exactly that key in Sprint 8).
 *
 * ⚠️ **What an Interviewer may see is the server's decision, never this screen's.** §5 gives all
 * three groups the Candidates item, and §6.3 gives an Interviewer only the candidates they hold an
 * interview on — enforced in `services.visible_considerations` and covered by
 * `backend/tests/test_permission_matrix.py`, so there is no group branch here and no client-side
 * filter. A refusal past that reach arrives as **404, never 403** (backend TASK-029) and is mapped
 * once, in `ApiErrorState`.
 */
export default function Candidates() {
  const { t } = useTranslation('candidates');
  const session = useSession();

  return (
    <>
      {/* §9: one `h1`, and it is the screen's own — in every one of §8's five states, including the
          ones where the table is a skeleton or a sentence. */}
      <h1 className={type.pageTitle}>{t('title')}</h1>

      {/*
       * ⚠️ **No viewer, no query key.** A disabled query still reads the cache at its key, so a
       * placeholder id would be one shared bucket for every session-less render of a payload that is
       * viewer-dependent (ADR 0006). The component that builds the key does not exist until there is
       * a viewer; until then this is §8's loading state, which is what it would render anyway.
       */}
      {session.data ? (
        <CandidateTable viewerId={session.data.user.id} groups={groupsOf(session.data)} />
      ) : (
        <LoadingRows />
      )}
    </>
  );
}

/** §8's loading state: rows shaped like rows, never a centred spinner. `Table` owns the shape. */
function LoadingRows() {
  return <Table columns={[]} rows={[]} rowKey={() => 0} isLoading />;
}

function CandidateTable({ viewerId, groups }: { viewerId: number; groups: AccessGroup[] }) {
  const { t, i18n } = useTranslation('candidates');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * The address bar is the filter (`candidateFilters.ts`), so a filtered list survives a reload and
   * the back button, the dashboard can link into it, and the chip below has one thing to read.
   */
  const filters = readCandidateFilters(searchParams);
  const list = useQuery(considerationsQuery(viewerId, toConsiderationQuery(filters)));

  /**
   * §6.4 is *"a dialog over `/candidates`"*, so its open state lives here and **not** in the URL: a
   * half-typed candidate is not an address to share, and a name in the query string is the one thing
   * this dialog must never produce (`AddCandidateDialog`). One instance for both of §6.3's buttons.
   */
  const [adding, setAdding] = useState(false);

  /**
   * Every control's change is a **new address**, which is what makes the back button undo a filter
   * (§6.3) and a filtered list linkable. Two details are decided here rather than in each control:
   *
   *  * the page is **not** carried over — a narrower filter has fewer pages, and page 4 of the
   *    previous filter is an empty table that reads as "no candidates";
   *  * the *defaulted* status is not written back either (`appliedFilterInput`), so changing the
   *    search does not silently freeze today's default into tomorrow's addresses.
   */
  const applyFilters = (patch: CandidateFilterInput) =>
    setSearchParams(candidateSearch({ ...appliedFilterInput(filters), ...patch }));

  const goToPage = (page: number) =>
    setSearchParams(candidateSearch({ ...appliedFilterInput(filters), page }), {
      // A page is a new address, not a correction of the previous one: Back goes back a page.
      replace: false,
    });

  const columns: Array<Column<ConsiderationRow>> = [
    {
      key: 'candidate',
      header: t('columns.candidate'),
      primary: true,
      cell: (row) => <CandidateCell row={row} />,
    },
    { key: 'vacancy', header: t('columns.vacancy'), cell: (row) => row.role_title },
    { key: 'status', header: t('columns.status'), cell: (row) => <StatusDot value={row.status} /> },
    {
      key: 'interviews',
      header: t('columns.interviews'),
      /**
       * §6.3: `3 / 4` — **submitted over total, straight from the payload**, with nothing computed
       * here.
       *
       * ⚠️ **The denominator counts drafts, on purpose** (ADR 0007's *"What the draft still counts
       * towards"*, and `backend/CONTEXT.md`'s derived-values table). `interview_count` is the one
       * sanctioned place a draft is counted, because this is a **visibility** number: the whole
       * point of the column is that an outstanding interview is visible without opening anything,
       * which is the leak the product exists to show. A draft is still never an input to
       * `average_score`, to `latest_recommendation` or to a status move — those are the same
       * payload's other three fields, and they are submitted-only.
       */
      cell: (row) => (
        <span className={cn(type.numeral, 'text-sm')}>
          {t('interviews.count', {
            submitted: row.submitted_interview_count,
            total: row.interview_count,
          })}
        </span>
      ),
    },
    {
      key: 'average',
      header: t('columns.average'),
      // Exactly as sent (ADR 0006). `null` is `Score`'s em dash — never a `0`, which would enter the
      // reader's arithmetic as a real average.
      cell: (row) => <Score value={row.average_score} />,
    },
    {
      key: 'latest',
      header: t('columns.latest'),
      // The document types this `OverallRecommendation | null` since backend TASK-104, so a verdict
      // outside the four is not on the wire; there is no client narrowing behind that any more
      // (BUG-038), and `StatusDot` renders nothing rather than throwing if one ever arrives.
      cell: (row) =>
        row.latest_recommendation ? (
          <StatusDot value={row.latest_recommendation} />
        ) : (
          <span className="text-sm text-ink-500" role="img" aria-label={t('latest.none')}>
            —
          </span>
        ),
    },
    {
      key: 'updated',
      header: t('columns.updated'),
      cell: (row) => (
        <Tooltip content={absoluteUpdatedAt(row.updated_at, i18n.language)}>
          <time dateTime={row.updated_at} className={type.metadata}>
            {relativeUpdatedAt(row.updated_at, i18n.language)}
          </time>
        </Tooltip>
      ),
    },
  ];

  if (list.isError) {
    /**
     * One mapping for every failure (`ApiErrorState`): offline, forbidden, throttled or the screen's
     * own sentence. ⚠️ A 404 here is a **collection** route answering, which means a broken
     * deployment and reads like one — the "outside your reach" mapping is deliberately scoped to a
     * record route (`/api/v1/considerations/42/`), because a list is filtered to a caller's reach
     * and never refused.
     */
    return (
      <div className="mt-10">
        <ApiErrorState
          error={list.error}
          message={t('error')}
          onRetry={() => void list.refetch()}
        />
      </div>
    );
  }

  const page = list.data;
  const rows = page?.items ?? [];
  const count = page?.count ?? 0;
  const first = rows.length === 0 ? 0 : (filters.page - 1) * CANDIDATE_PAGE_SIZE + 1;
  const last = rows.length === 0 ? 0 : first + rows.length - 1;

  return (
    <>
      {/*
       * §6.3's controls, including the chip: *"a visible chip showing a filter is applied, because a
       * filtered list that looks unfiltered is how people conclude a candidate was lost."* They are
       * **above the table in every state** — including both empty ones, which is the whole point: a
       * filtered-empty screen that hides its filters leaves the reader unable to see why they see
       * nothing.
       *
       * `rows` goes in because one filter has to be built out of them: an Interviewer may not read
       * the vacancy catalogue (`VacancyFilter`).
       */}
      <FilterBar
        filters={filters}
        groups={groups}
        viewerId={viewerId}
        rows={rows}
        onChange={applyFilters}
        onAdd={() => setAdding(true)}
      />

      <div className="mt-6">
        <Table
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          isLoading={list.isPending}
          /**
           * §6.3: *"Each row links to the person's card."* The anchor lives in the primary cell —
           * it is what tells a screen reader the row can be activated at all, which `Table`'s own
           * docblock defers to *"§6.3's candidate table, the first one with a URL to give"* — and the
           * whole row mirrors it for the mouse. Both go to the same address; `CandidateCell` stops
           * the click from reaching the row so one activation is one history entry.
           */
          onRowActivate={(row) => void navigate(personPath(row))}
          rowLabel={(row) => t('row.open', { name: displayName(row, t('anonymized')) })}
          /**
           * ⚠️ **§8's two empty states, which are two states and not one sentence with a variable
           * in it.** *"No candidates yet."* is a tenant with nothing in it and its action is the one
           * thing that helps — **Add candidate**, and only for a viewer who may
           * (`AddCandidateButton`). *"No candidates match these filters."* is a filter hiding rows
           * and its action is the one thing that helps there — **Clear filters**. Offering "add a
           * candidate" to somebody whose search simply missed is how a duplicate person gets
           * created, which is ADR 0002's whole subject.
           *
           * Which of the two is `couldBeHidingRows`, and its docblock states the one case the client
           * cannot distinguish honestly and what is done about it.
           */
          empty={
            couldBeHidingRows(filters) ? (
              <EmptyState
                icon={SearchX}
                message={t('empty.filtered')}
                action={<ClearFilters onChange={applyFilters} />}
              />
            ) : (
              <EmptyState
                icon={Users}
                message={t('empty.none')}
                action={<AddCandidateButton groups={groups} onClick={() => setAdding(true)} />}
              />
            )
          }
        />
      </div>

      {/*
       * §6.3 says nothing about pagination and the API truncates at 50 (`CANDIDATE_PAGE_SIZE`), so a
       * list that is cut off must say so — a table that ends at row 50 with no total is a list a
       * recruiter reads as complete. `count` is the payload's own total for the filter, stated with
       * the range on screen. `DESIGN.md` §6.3 is extended with this in the same pass.
       */}
      {count > 0 && (
        <nav
          className="mt-4 flex flex-wrap items-center justify-between gap-3"
          aria-label={t('pagination.label')}
        >
          <p className={type.metadata}>{t('pagination.range', { first, last, count })}</p>
          <span className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={filters.page <= 1}
              onClick={() => goToPage(filters.page - 1)}
            >
              {t('pagination.previous')}
            </Button>
            {/*
             * ⚠️ `rows.length === 0` is not redundant with `last >= count` — sprint-end review
             * finding (`code-reviewer`, 2026-07-28). On an empty page `first` and `last` are both
             * `0`, so `0 >= 137` is false and *Next* stayed live on `?page=99`, walking to page 100
             * and every page after it. An empty page has no next page whatever the total says.
             */}
            <Button
              variant="secondary"
              size="sm"
              disabled={rows.length === 0 || last >= count}
              onClick={() => goToPage(filters.page + 1)}
            >
              {t('pagination.next')}
            </Button>
          </span>
        </nav>
      )}

      {/*
       * §6.4's dialog, mounted once and only for a viewer who may write.
       *
       * ⚠️ **The group check is here as well as on the button** because this component asks the API
       * for something: the dialog reads the vacancy catalogue, and `GET /api/v1/roles/` answers an
       * Interviewer 403 (`candidateAccess.ts` cites the matrix row; `candidateControls.test.tsx`
       * asserts this screen makes no request to it for them). Not rendering it is how that stays true
       * even if the dialog later asks for something on mount.
       */}
      {mayAddCandidate(groups) && (
        <AddCandidateDialog
          open={adding}
          onOpenChange={setAdding}
          viewerId={viewerId}
          // Only to tell the two groups apart in the sentence an **empty** vacancy catalogue gets, and
          // to withhold the way to §6.8 from the group that may not create one (BUG-020).
          groups={groups}
        />
      )}
    </>
  );
}

/**
 * The filter as a link input: the **defaulted** status is deliberately not written back, so paging
 * or clearing does not silently freeze today's default into tomorrow's addresses.
 */
function appliedFilterInput(filters: CandidateFilters) {
  return { status: filters.status, vacancy: filters.vacancy, search: filters.search };
}

const personPath = (row: ConsiderationRow) => `/candidates/${row.person_id}`;

/**
 * ⚠️ **An erasure is read from the field that means it since backend TASK-110** —
 * `person_anonymized_at` — and it was **inferred from a blank name** until then. The inference was
 * true (`anonymize_person` blanks `full_name` in place and keeps the row, every consideration and
 * every score — ADR 0009). ⚠️ **The reason given for replacing it does not hold and is retracted here**
 * *(BUG-041, 2026-08-06)*: it was *"a rule that would quietly become wrong the day a name is
 * legitimately blank"*, and that is not a day this API allows — the third paragraph below names the
 * constraint that prevents it. The replacement is still right, for the reason the fourth paragraph
 * gives. The alternative at the time was a request per row to the card endpoint, which is why the
 * inference shipped and why the field was a Backlog item rather than a defect.
 *
 * ⚠️ **This comment claimed a fallback that does not exist, and the claim is what BUG-041 was about**
 * *(corrected 2026-08-06)*. It read: *"a blank name that is not an erasure now renders as a blank
 * name, … and the cell still never renders empty"* — two sentences that contradict each other, and the
 * second was the false one. Nothing here supplies anything: with `person_full_name: ''` and
 * `person_anonymized_at: null` this returns `''`, and `<Link>{''}</Link>` is a link with **no
 * accessible name** — precisely the broken row the sentence forbade.
 *
 * **The state is prevented by the API rather than handled here, and naming the constraint is the fix.**
 * `PersonIn.full_name` and `PersonUpdate.full_name` both carry `min_length=1`, so neither a create nor
 * an edit can write an empty name, and `personFieldsSchema` trims before either is sent. That claim is
 * **asserted against the OpenAPI document** in `api/candidates.test.tsx`, so dropping the constraint
 * turns a test red here instead of quietly turning this paragraph back into fiction. A fallback for a
 * state the schema forbids would be a branch nothing can reach and a claim nothing can test — the
 * *"criterion nothing can fail"* shape one level down. ⚠️ §6.10's staff half of the same bug got the
 * **opposite** answer for the opposite reason: `StaffOut.name` carries no `minLength` at all, so
 * `pages/Settings/staffName.ts` is a floor there.
 *
 * ⚠️ **The retraction in the first paragraph, in full**: the case TASK-110 was justified by — a name
 * *"legitimately blank"* — is one the API prevents. The field is still the right thing to read, for a
 * better reason: it keeps the erasure label off a row that merely lacks a name **and** the name off a
 * row that was erased, which a stale payload can still be. `candidateTable.test.tsx` drives both
 * directions and asserts what is **present** in each — the old test asserted only that *"Anonymized"*
 * was absent, which a cell rendering nothing at all also satisfies.
 *
 * ⚠️ **The one residual, named rather than papered over**: `min_length=1` counts characters, so a name
 * of a single space satisfies it (measured against this project's own pydantic 2.13) and
 * `Person.full_name` is stored verbatim — only its normalised twin is stripped. Nothing in this
 * repository can send one, so it takes a direct API call; the fix belongs to the constraint and is
 * recorded in **BUG-052**, not to a client fallback that would make the schema's word untrustworthy
 * here. (This pointer said BUG-041 until that bug's own review caught it — BUG-041's entry says
 * nothing about whitespace, so a reader following it found nothing, which is the exact failure the
 * comment exists to prevent.)
 */
function displayName(row: ConsiderationRow, anonymizedLabel: string): string {
  return row.person_anonymized_at !== null ? anonymizedLabel : row.person_full_name;
}

function CandidateCell({ row }: { row: ConsiderationRow }) {
  const { t } = useTranslation('candidates');
  const anonymized = row.person_anonymized_at !== null;
  const name = displayName(row, t('anonymized'));

  return (
    <span className="flex min-w-0 items-center gap-3">
      <Avatar name={name} />
      <span className="min-w-0">
        <Link
          to={personPath(row)}
          // The row carries the same click, so without this one activation would push two identical
          // history entries and Back would appear not to work.
          onClick={(event) => event.stopPropagation()}
          className={cn(
            'rounded text-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
            anonymized ? 'text-ink-500' : 'text-ink-900',
          )}
        >
          {name}
        </Link>
        {/* §6.3: source beneath the name. An anonymised person has none — it is cleared with the
            name — and an empty line is not rendered. */}
        {row.person_source !== '' && (
          <span className={cn(type.metadata, 'block')}>{row.person_source}</span>
        )}

        {/*
         * ⚠️ **ADR 0021's warning on the row, which is the half §6.3 was owed and could not render**
         * (TASK-179). The ADR puts it *"where a recruiter will actually meet it — on the candidate
         * list row **and** on the person card"*, and until the document carried
         * `possible_duplicate_count` there was nothing on this payload to render it from: §6.3 said
         * so in as many words, refused a request per row to the card endpoint on the screen the
         * product lives on, and recorded the missing key as a backend item. The key exists, so this
         * is it.
         *
         * ⚠️ **A count and not a boolean, and the sentence spends it.** A flag would say *"this may
         * be a duplicate"* and nothing else; from a count a screen can say **how many other cards**,
         * which is the difference between a reader expecting one card to compare against and a
         * reader expecting two. `_one`/`_other` on it for the reason every counted string in this
         * product carries them — *"1 sets"* and *"1 hours"* both reached live passes.
         *
         * **The row says that, and no more.** Which card and which key matched are the **card's** to
         * say (`PossibleDuplicates`): they are ids and key names that have nowhere to go in a cell,
         * and a list of them on a page of fifty rows would be a payload with no reader. The row is
         * one `person_id` away from the card that answers it, and the whole row is already a link to
         * exactly that.
         *
         * ⚠️ **It survives an erasure, like the warning it reflects.** The stored row holds ids and
         * key names and nothing personal, so ADR 0009 clears nothing on it; an anonymised person's
         * row renders *Anonymized* and this line together, which is true and is the state §6.5
         * renders too.
         *
         * `ink-700` rather than `negative`: §3 keeps colour for meaning and this is a standing fact
         * somebody has to read, not a fault in this row. The screen does not shout — the card does
         * the arguing.
         */}
        {row.possible_duplicate_count > 0 && (
          <span className={cn(type.metadata, 'block text-ink-700')}>
            {t('row.possibleDuplicate', { count: row.possible_duplicate_count })}
          </span>
        )}
      </span>
    </span>
  );
}
